import { ChangeDetectionStrategy, Component, computed, inject, input, model, resource } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsButtonModule, DsCheckboxModule, DsLabelModule, DsMessageModule } from '@bmw-ds/components';

import { DealerProvider } from '../../../../auth/dealer/dealer.provider';
import { RetailCrmService } from '../../../customer/clients/retail-crm/retail-crm.service';
import { CustomerVehicle, CustomerVehicleRelationType } from '../../../customer/model/customer-vehicle.model';
import { FoundCustomer } from '../../../customer/model/found-customer.model';
import { CustomerService } from '../../../customer/services/customer.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { LoadingIndicatorComponent } from '../../../loading/loading-indicator.component';
import { VehicleInfoComponent } from '../vehicle-info/vehicle-info.component';
import { AddRelationModalComponent } from './add-relation-modal.component';

@Component({
  selector: 'cdf-add-relation-modal-container',
  imports: [
    DsButtonModule,
    VehicleInfoComponent,
    DsCheckboxModule,
    DsLabelModule,
    FormsModule,
    AddRelationModalComponent,
    DsMessageModule,
    LoadingIndicatorComponent,
  ],
  template: `
    <cdf-add-relation
      [(selectedRelations)]="selectedRelations"
      [existingRelations]="vehicle().relationTypes"
      [otherCustomerRelations]="otherCustomerRelations()"
    >
      <cdf-vehicle-info [vehicle]="vehicle()" [activeCountry]="dealerProvider.activeCountry()" smallImage />
    </cdf-add-relation>

    @if (showStatusLine()) {
      <div class="pt-2x" style="min-height: 40px">
        @if (vehicleResource.status() === 'loading') {
          <cdf-loading-indicator>{{
            i18n('vehicles.addRelationVerifyModal.loadingOtherCustomers')
          }}</cdf-loading-indicator>
        } @else if (vehicleResource.status() === 'error') {
          <ds-message
            variant="slim"
            tone="caution"
            [messageText]="i18n('vehicles.addRelationVerifyModal.loadOtherCustomersError')"
          />
        } @else if (!hasOtherCustomers()) {
          <ds-message
            variant="slim"
            tone="info"
            [messageText]="i18n('vehicles.addRelationVerifyModal.noOtherCustomers')"
          />
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddRelationModalContainer {
  protected readonly i18n = useI18n();
  private readonly customerService = inject(CustomerService);
  private readonly retailCrmService = inject(RetailCrmService);
  protected readonly dealerProvider = inject(DealerProvider);

  readonly vehicle = input.required<CustomerVehicle>();
  readonly allCustomersOverride = input<Partial<Record<CustomerVehicleRelationType, FoundCustomer>>>();

  protected readonly vehicleResource = resource({
    params: () => (this.allCustomersOverride() === undefined ? this.vehicle().vin : undefined),
    loader: ({ params: vin }) => this.retailCrmService.getVehicle(vin),
  });

  protected readonly showStatusLine = computed(() => this.allCustomersOverride() === undefined);

  protected readonly otherCustomerRelations = computed(() => {
    const allCustomers =
      this.allCustomersOverride() ??
      (this.vehicleResource.hasValue() ? this.vehicleResource.value()?.allCustomers : undefined);
    const relations: Partial<Record<CustomerVehicleRelationType, string>> = {};
    for (const relation of Object.values(CustomerVehicleRelationType)) {
      const customer = allCustomers?.[relation];
      if (customer) {
        relations[relation] = this.customerService.fullName(customer);
      }
    }
    return relations;
  });

  protected readonly hasOtherCustomers = computed(() =>
    Object.keys(this.otherCustomerRelations()).some(
      (r) => !this.vehicle().relationTypes.includes(r as CustomerVehicleRelationType),
    ),
  );

  readonly selectedRelations = model.required<ReadonlySet<CustomerVehicleRelationType>>();
}
